import { Banknote, FileTextIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { capitalizeWithSpaces, ScrollToElement } from "utils";
import type { BidPostData } from "validations";

import { OnboardingTask } from "@krea/lender-features/components/onboarding-task";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@krea/shared/design-system/accordion";
import { Badge } from "@krea/shared/design-system/Badge";
import { Button } from "@krea/shared/design-system/Button";
import { Spinner } from "@krea/shared/design-system/Spinner";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@krea/shared/design-system/Tabs";
import { PromotionTypeEnum } from "@krea/shared/types/common";
import { cn } from "@krea/shared/utils";

import {
  LenderApplicationTypeEnum,
  type OnboardingStepEnum,
} from "@/features/common/enums";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import { useChangeOnboardingStatus } from "@/features/lender-responses/hooks/data/use-change-onboarding-status.mutation";
import type {
  IPromotion,
  IPromotionInput,
} from "@/features/lender-responses/types/lender-response";

import { useLenderApplicationDetailsStore } from "../lenderApplicationDetailsStore";
import { BidDetails, BidSummary } from "./BidDetails";
import {
  bidFramerVariants,
  getFormValuesFromLenderResponse,
  type IBidProps,
  useLenderResponseCommonProperties,
} from "./bidHelpers";
import LenderResponseFieldRequestDetails from "./field-requests/LenderResponseFieldRequestDetails";

interface ILenderResponseProps {
  lenderResponse: IBidProps["lenderResponse"];
  preview: IBidProps["preview"];
  lenderApplication: IBidProps["lenderApplication"];
  isPreviewLoading?: boolean;
}

export const LenderResponse = ({
  lenderResponse,
  preview,
  lenderApplication,
  isPreviewLoading,
}: ILenderResponseProps) => {
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const { type, loanApplication } = lenderApplication;
  const { currency } = loanApplication;
  const { reset } = useFormContext<BidPostData>();
  const setCurrentlyUpdatingLenderResponseId = useLenderApplicationDetailsStore(
    (state) => state.setCurrentlyUpdatingLenderResponseId,
  );

  const {
    mutate: updateOnboardingSteps,
    isPending: isLoadingUpdateOnboarding,
  } = useChangeOnboardingStatus();

  const onChangeBoardingFields = useCallback(
    (checked: boolean, field: OnboardingStepEnum) => {
      if (!("id" in lenderResponse)) return;

      updateOnboardingSteps({
        data: {
          steps: [{ type: field, value: checked }],
        },
        lenderCompanyId: Number(lenderCompany?.id),
        lenderResponseId: lenderResponse.id,
      });
    },
    [lenderResponse, lenderCompany?.id, updateOnboardingSteps],
  );

  const onUpdate = useCallback(() => {
    if (!("id" in lenderResponse)) return;

    setCurrentlyUpdatingLenderResponseId(lenderResponse.id);

    const values = getFormValuesFromLenderResponse(
      lenderResponse,
      lenderCompany?.isFeatureInterestRatePerYearActivated,
    );

    reset(values);
  }, [
    lenderResponse,
    reset,
    setCurrentlyUpdatingLenderResponseId,
    lenderCompany?.isFeatureInterestRatePerYearActivated,
  ]);

  const {
    additionalInformationFieldRequests,
    collateralFieldRequests,
    dateCreated,
    dateUpdated,
    isOpen,
    isUpdated,
    onDelete,
    readMoreText,
    showMore,
    statusText,
    statusVariant,
    isSelectedLenderResponse,
  } = useLenderResponseCommonProperties({
    lenderResponse,
    lenderApplication,
    preview,
  });

  const getPromotionAsText = useCallback(
    (promotion: IPromotion | IPromotionInput): string => {
      if (!promotion.value) return ""; // Should never happen, but just in case

      const prefixText =
        promotion.value === 1
          ? "First month"
          : `First ${promotion.value} months`;

      switch (promotion.type) {
        case PromotionTypeEnum.INTEREST_FREE:
          return `${prefixText} without interest`;
        case PromotionTypeEnum.AMORTISATION_FREE:
          return `${prefixText} without amortization`;
      }
    },
    [],
  );

  return (
    <AnimatePresence>
      <motion.div
        key={
          "id" in lenderResponse
            ? `${lenderResponse.bids[0]?.type}-${lenderResponse.id}`
            : `new`
        }
        initial="initial"
        exit="hidden"
        animate="visible"
        variants={bidFramerVariants}
        className={cn("shadow bg-white p-4", {
          "shadow-lg": isSelectedLenderResponse,
        })}
      >
        <div
          className={cn({
            "pt-2": preview,
          })}
        >
          {!preview && statusVariant && (
            <div className="flex flex-col flex-wrap justify-between gap-2 text-sm lg:flex-row ">
              <div className="flex flex-col flex-wrap items-start gap-2 text-sm lg:flex-row ">
                <Badge
                  className="items-center uppercase"
                  variant={statusVariant}
                >
                  {statusText}
                </Badge>
                <div className="flex flex-row items-start gap-1">
                  <div>Created:</div>
                  <span className="text-gray-500">{dateCreated}</span>
                </div>
                {isUpdated && (
                  <div className="flex flex-row items-start gap-1">
                    <div>Updated:</div>
                    <span className="text-gray-500">{dateUpdated}</span>
                  </div>
                )}
                {"benefits" in lenderResponse && (
                  <div className="flex flex-row items-start gap-1">
                    <div>Benefits:</div>

                    <ul className="pl-0">
                      {lenderResponse.benefits?.map((benefit, index) => (
                        <li
                          key={index}
                          className="flex items-center text-gray-500"
                        >
                          {benefit.displayName}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="flex flex-col flex-wrap items-start gap-2 text-sm lg:flex-row ">
                {"creditTermsFile" in lenderResponse &&
                  lenderResponse.creditTermsFile?.fileUrl && (
                    <Button
                      variant="ghost"
                      className="my-1 text-primary"
                      onClick={() => {
                        if (lenderResponse.creditTermsFile?.fileUrl) {
                          window.open(
                            lenderResponse.creditTermsFile.fileUrl,
                            "_blank",
                          );
                        }
                      }}
                    >
                      <div className="flex items-center gap-2 text-sm">
                        <FileTextIcon className="size-5 cursor-pointer" />
                        <span>Termsheet</span>
                      </div>
                    </Button>
                  )}
              </div>
            </div>
          )}

          <div className="mt-2 w-full rounded-md border border-l-8 border-solid border-gray-300 border-l-primary  p-3">
            {preview && lenderResponse.bids.length === 0 ? (
              <div className="rounded bg-white p-6 text-center">
                {isPreviewLoading ? (
                  <Spinner className="my-2 size-10 text-primary" />
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex flex-col items-center gap-1 rounded-lg bg-white p-4">
                      <div className="flex size-8 items-center justify-center rounded-full  text-primary">
                        <Banknote className="size-8" strokeWidth={1.5} />
                      </div>
                      <p className="m-0 text-sm font-medium text-black">
                        No offer preview yet
                      </p>
                      <p className="m-0 text-sm leading-relaxed text-gray-500">
                        Complete the form with valid values to see your offer
                        preview here.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                {lenderResponse.bids.map((bid, index) => (
                  <div key={`${index}-${bid.type}`}>
                    <div className="flex w-full flex-col items-stretch gap-x-8 gap-y-3 py-3 pl-2 xl:flex-row xl:flex-wrap xl:items-start">
                      <p className="m-0 shrink-0 text-base font-bold capitalize ">
                        {capitalizeWithSpaces(bid.type)}
                      </p>
                      <div className="w-full ">
                        <BidSummary
                          key={`${index}-${bid.type}`}
                          bid={bid}
                          currency={currency}
                        />
                      </div>
                    </div>
                    {index < lenderResponse.bids.length - 1 && (
                      <hr className="my-0 border-solid border-gray-300" />
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
          {isSelectedLenderResponse && (
            <OnboardingTask
              onboardingStartedAt={lenderResponse.onboardingStartedAt}
              creditAssessmentDoneAt={lenderResponse.creditAssessmentDoneAt}
              onChange={(value, type) =>
                onChangeBoardingFields(value, type as OnboardingStepEnum)
              }
              disabled={
                ("id" in lenderResponse && !!lenderResponse.paidOutAt) ||
                isLoadingUpdateOnboarding
              }
              signingLinkClickedAt={lenderResponse.signingLinkClickedAt}
            />
          )}

          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.div
                initial="collapsed"
                animate="open"
                exit="collapsed"
                variants={{
                  open: {
                    opacity: 1,
                    height: "auto",
                    transition: {
                      type: "tween",
                      duration: 0.1,
                      ease: "easeOut",
                    },
                  },
                  collapsed: {
                    opacity: 0,
                    height: 0,
                    transition: {
                      type: "tween",
                      duration: 0.1,
                      ease: "easeOut",
                    },
                  },
                }}
                transition={{ duration: 0.1 }}
                className="pb-4 pt-5"
              >
                <div className="flex flex-col gap-4 lg:flex-row">
                  <div className="w-full rounded border border-gray-200 p-4 lg:w-1/2">
                    <>
                      {collateralFieldRequests.length > 0 && (
                        <LenderResponseFieldRequestDetails
                          title={"Collateral / Securities"}
                          fieldRequests={collateralFieldRequests}
                        />
                      )}

                      {additionalInformationFieldRequests.length > 0 && (
                        <LenderResponseFieldRequestDetails
                          title={"Additional Data Request"}
                          fieldRequests={additionalInformationFieldRequests}
                        />
                      )}
                    </>
                  </div>
                  <div className="mb-5 w-full rounded-md border border-solid border-gray-200 bg-white p-4 lg:mb-0 lg:w-1/2">
                    {/* {detailsContent} */}
                    <Tabs defaultValue={`0-${lenderResponse.bids[0]?.type}`}>
                      <TabsList>
                        {lenderResponse.bids.map((bid, index) => (
                          <TabsTrigger
                            key={`${index}-${bid.type}`}
                            value={`${index}-${bid.type}`}
                          >
                            {capitalizeWithSpaces(bid.type)}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      {lenderResponse.bids.map((bid, index) => (
                        <TabsContent
                          key={`${index}-${bid.type}`}
                          value={`${index}-${bid.type}`}
                        >
                          <BidDetails
                            key={`${index}-${bid.type}-details`}
                            bid={bid}
                            currency={currency}
                          />
                        </TabsContent>
                      ))}
                    </Tabs>
                  </div>
                </div>

                {lenderCompany?.name && lenderCompany.description && (
                  <Accordion
                    type="multiple"
                    className="mt-4 flex flex-col gap-3"
                  >
                    <AccordionItem
                      key={type}
                      value={type}
                      className="relative rounded border pl-3 pr-2"
                    >
                      <AccordionTrigger className="">
                        <div> {`About ${lenderCompany.name}`} </div>
                      </AccordionTrigger>

                      <AccordionContent>
                        <>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: lenderCompany.description,
                            }}
                          />
                          {lenderCompany.registeredAtFinansinspektionenAt && (
                            <div className="mt-3 text-center text-gray-500">
                              Approved by Finansinspektionen
                            </div>
                          )}
                        </>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {lenderResponse.promotions &&
            lenderResponse.promotions.filter((it) => it.value && it.value > 0)
              .length > 0 && (
              <div className="-mx-4 -mb-3 mt-2 rounded-t bg-mint-100 p-2 text-sm">
                <div className="px-2">
                  <span className="">Special offer: </span>
                  <span className="font-medium">
                    {lenderResponse.promotions
                      .map((it) => {
                        return getPromotionAsText(it);
                      })
                      .join(", ")}
                  </span>
                </div>
              </div>
            )}

          <div className="-mx-4 mt-3 border-t border-gray-200 px-4">
            <div className="flex justify-end gap-4 pt-4">
              <Button variant="secondary" onClick={showMore}>
                {readMoreText}
              </Button>

              {!(
                preview ||
                !("isActive" in lenderResponse && lenderResponse.isActive) ||
                type ===
                  LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED ||
                type ===
                  LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER ||
                type ===
                  LenderApplicationTypeEnum.OTHER_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER ||
                type ===
                  LenderApplicationTypeEnum.FAILED_LENDER_APPLICATION_FILTER
              ) && (
                <>
                  <Button
                    onClick={() => {
                      onUpdate();
                      ScrollToElement("scrollToBidsForm");
                    }}
                  >
                    Edit
                  </Button>
                  <Button variant="danger" onClick={onDelete}>
                    Remove
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
